import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';
import { ArrowUpCircle, AlertCircle, CheckCircle, Wallet } from 'lucide-react';

export default function WithdrawPage() {
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [accountDetails, setAccountDetails] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const queryClient = useQueryClient();
  const { data: wallet } = useQuery({ queryKey: ['wallet'], queryFn: () => api.get('/wallet').then(r => r.data) });
  const mutation = useMutation({
    mutationFn: (data: any) => api.post('/withdrawals', data),
    onSuccess: () => { setSuccess(true); setAmount(''); setAccountDetails(''); queryClient.invalidateQueries({ queryKey: ['wallet'] }); },
    onError: (err: any) => { setError(err.response?.data?.message || 'Withdrawal failed'); },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) { setError('Enter valid amount'); return; }
    setError('');
    mutation.mutate({ amount: amt, paymentMethod, accountDetails });
  };

  return (
    <div className="max-w-lg mx-auto space-y-6 animate-fade-in">
      <div className="d-page-header">
        <h1 className="text-2xl font-bold text-white">Withdraw Funds</h1>
        <p className="text-slate-400 mt-1">Withdraw your earnings.</p>
      </div>

      <div className="d-card p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <p className="text-sm text-slate-400">Available Balance</p>
            <p className="text-xl font-bold text-white">{formatCurrency(wallet?.balance || 0)}</p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader><h2 className="text-base font-semibold text-white">Request Withdrawal</h2></CardHeader>
        <CardContent>
          {success && (
            <div className="flex items-center gap-2.5 p-3.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-sm text-emerald-400 mb-4">
              <CheckCircle className="w-4 h-4 shrink-0" />Withdrawal request submitted successfully.
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2.5 p-3.5 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400 mb-4">
              <AlertCircle className="w-4 h-4 shrink-0" />{error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input label="Amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter amount" required />
            <Select label="Payment Method" options={[{ value: 'bank_transfer', label: 'Bank Transfer' }, { value: 'upi', label: 'UPI' }, { value: 'usdt_trc20', label: 'USDT (TRC20)' }, { value: 'usdt_erc20', label: 'USDT (ERC20)' }]} value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} />
            <Input label="Account Details" value={accountDetails} onChange={(e) => setAccountDetails(e.target.value)} placeholder="Bank account / UPI ID / Wallet address" />
            <Button type="submit" isLoading={mutation.isPending} className="w-full">
              <ArrowUpCircle className="w-4 h-4" />Submit Withdrawal
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
